import { Controller, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  InputLabel,
  Text,
  TextInput,
  Textarea,
  Grid,
  Select,
  Anchor,
  Checkbox,
  Popover,
} from "@mantine/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  faCircleExclamation,
  faInfoCircle,
} from "@fortawesome/sharp-solid-svg-icons";
import {
  faMagnifyingGlass,
  faMobile,
  faQuestionCircle,
} from "@fortawesome/sharp-regular-svg-icons";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FeatureToggle } from "shared/src/constants/feature-toggle-names";
import { useAllowSucraid } from "@/hooks/use-allow-sucraid";
import states from "@/data/states.json";
import { InputMaybe, ProviderFacility, Scalars } from "@/__generated__/graphql";
import {
  Condition,
  getConditionsTreatedByMedication,
  getMedicationsForReferral,
  isGlp1Condition,
  Medication,
} from "shared/src/data/medication-condition-pairs";
import { DenialReason } from "@/data/denial-reasons";
import { Tip } from "design-system/src/components/tip";
import { subDenialReasonOptionsByCondition } from "@/pages/refer-patient/refer-patient";
import { FdaEligibilityPopoverContent } from "./fda-eligibility-popover-content";

import styles from "./refer-patient.module.scss";

const phoneRegex = /^(\+?1[-.\s]?)?(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}$/;

export type ReferralFormInput = {
  firstName?: InputMaybe<Scalars["String"]["input"]>;
  lastName?: InputMaybe<Scalars["String"]["input"]>;
  patientEmail?: InputMaybe<Scalars["String"]["input"]>;
  phoneNumber?: InputMaybe<Scalars["String"]["input"]>;
  hasConsentedToSMSNotifications?: boolean;
  dateOfBirth?: InputMaybe<Scalars["String"]["input"]>;
  condition?: InputMaybe<Scalars["String"]["input"]>;
  state?: InputMaybe<Scalars["String"]["input"]>;
  medication?: InputMaybe<Scalars["String"]["input"]>;
  denialReason?: InputMaybe<Scalars["String"]["input"]>;
  subDenialReason?: InputMaybe<Scalars["String"]["input"]>;
  message?: InputMaybe<Scalars["String"]["input"]>;
  meetsFdaGuidelines?: boolean;
  providerFacilityId?: string;
};

export type ReferralFormProps = {
  formId: string;
  onSubmit: (formData: ReferralFormInput) => Promise<void>;
  providerFacilities: ProviderFacility[];
};

const fdaGuidelinesSchema = z
  .object({
    condition: z.string(),
    meetsFdaGuidelines: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      ([Condition.OBESITY, Condition.TYPE_2_DIABETES] as string[]).includes(
        data.condition,
      ) &&
      data.meetsFdaGuidelines !== true
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "You must confirm the patient meets FDA guidelines for GLP-1",
        path: ["meetsFdaGuidelines"],
      });
    }
  });

const referralFormSchema = z
  .object({
    firstName: z.string().min(1, { message: "First name is required" }),
    lastName: z.string().min(1, { message: "Last name is required" }),
    patientEmail: z
      .string({ message: "Email is required" })
      .email({ message: "Invalid email" }),
    dateOfBirth: z
      .string({ message: "Date of birth is required" })
      .refine(
        (dateStr) => {
          const date = new Date(dateStr);
          const today = new Date();
          const eighteenYearsAgo = new Date(
            today.getFullYear() - 18,
            today.getMonth(),
            today.getDate(),
          );
          return date <= eighteenYearsAgo;
        },
        { message: "The patient must be at least 18 years old" },
      )
      .refine(
        (dateStr) => {
          const date = new Date(dateStr);
          return date.getFullYear() >= 1900;
        },
        { message: "Date must be after year 1900" },
      ),
    state: z.string({ message: "State is required" }),
    condition: z.string({ message: "Condition is required" }),
    medication: z.string({ message: "Medication is required" }),
    denialReason: z.string({ message: "Denial Reason is required" }),
    message: z.string({ message: "A message is required" }),
    subDenialReason: z.string().optional(),
    meetsFdaGuidelines: z.boolean().optional(),
    phoneNumber: z.string().optional(),
    hasConsentedToSMSNotifications: z.boolean().optional(),
  })
  .superRefine(
    ({ phoneNumber, hasConsentedToSMSNotifications }, schemaContext): void => {
      if (phoneNumber && !hasConsentedToSMSNotifications) {
        schemaContext.addIssue({
          code: "custom",
          message:
            "Consent is required to send patients SMS notifications. If you would like to not send any, please leave the phone number field blank.",
          path: ["hasConsentedToSMSNotifications"],
        });
      } else if (phoneNumber?.trim().length && !phoneRegex.test(phoneNumber)) {
        schemaContext.addIssue({
          code: "custom",
          message: "Enter a valid phone number",
          path: ["phoneNumber"],
        });
      }

      if (!phoneNumber && hasConsentedToSMSNotifications) {
        schemaContext.addIssue({
          code: "custom",
          message:
            "Patient mobile number is required to send SMS notifications. If you’d like to not send any, please uncheck the consent box.",
          path: ["phoneNumber"],
        });
      }
    },
  );

const getReferralFormSchema = (isFacilityRequired: boolean) =>
  referralFormSchema.and(fdaGuidelinesSchema).and(
    isFacilityRequired
      ? z.object({
          providerFacilityId: z.string({ message: "Facility is required" }),
        })
      : z.object({
          providerFacilityId: z.string().optional(),
        }),
  );

const dupixentConditions: string[] = [
  Condition.ATOPIC_DERMATITIS,
  Condition.CHRONIC_RHINOSINUSITIS,
  Condition.EOSINOPHILIC_ESOPHAGITIS,
  Condition.PRURIGO_NODULARIS,
  Condition.COPD,
  Condition.CHRONIC_SPONTANEOUS_URTICARIA,
  Condition.BULLOUS_PEMPHIGOID,
];

export function getDenialReasons(
  selectedCondition: string | null | undefined,
  medication: string | null | undefined,
): DenialReason[] {
  return Object.values(DenialReason)
    .filter((reason) => {
      if (reason === DenialReason.STEP_THERAPY_REQUIRED) {
        return true;
      }
      if (reason === DenialReason.NOT_A_COVERED_BENEFIT) {
        return selectedCondition === Condition.OBESITY;
      }
      if (reason === DenialReason.ANTICIPATED_FORMULARY_CHANGE) {
        return Boolean(
          selectedCondition &&
          (dupixentConditions.includes(selectedCondition) ||
            (selectedCondition === Condition.ASTHMA &&
              medication === Medication.DUPIXENT)),
        );
      }
      return true;
    })
    .sort();
}

function ReferralForm({
  formId,
  onSubmit,
  providerFacilities = [],
}: ReferralFormProps) {
  const isCsidSucraidEnabled = useAllowSucraid();
  const isHppStrensiqEnabled =
    useFeatureFlagEnabled(FeatureToggle.HPP_STRENSIQ) === true;

  const isFacilityRequired = providerFacilities.length > 0;

  const schema = getReferralFormSchema(isFacilityRequired);

  const {
    control,
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<ReferralFormInput>({
    defaultValues: {
      firstName: undefined,
      lastName: undefined,
      patientEmail: undefined,
      phoneNumber: undefined,
      hasConsentedToSMSNotifications: false,
      dateOfBirth: undefined,
      state: undefined,
      condition: undefined,
      medication: undefined,
      denialReason: undefined,
      message: undefined,
      subDenialReason: undefined,
      meetsFdaGuidelines: false,
    },
    resolver: zodResolver(schema) as any,
    mode: "all",
  });

  const medication = watch("medication");
  const selectedDenialReason = watch("denialReason");
  const selectedCondition = watch("condition");

  const denialReasons = getDenialReasons(selectedCondition, medication);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col>
          <Controller
            name="medication"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="Medication"
                placeholder="Look up by name"
                data-testid="medication-input"
                searchable
                error={errors.medication?.message}
                data={getMedicationsForReferral({
                  conditions: {
                    [Condition.PANDAS]: false,
                    [Condition.PANS]: false,
                    [Condition.CSID]: isCsidSucraidEnabled,
                    [Condition.HYPOPHOSPHATASIA]: isHppStrensiqEnabled,
                  },
                })}
                rightSection={
                  <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
                }
                withAsterisk
              />
            )}
          />
        </Grid.Col>
      </Grid>
      <Grid mt="md">
        <Grid.Col>
          <Controller
            name="condition"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="Condition"
                placeholder="Look up by condition"
                data-testid="condition-input"
                searchable
                error={errors.condition?.message}
                disabled={!medication}
                data={
                  medication
                    ? [
                        ...getConditionsTreatedByMedication(
                          medication as Medication,
                        ),
                      ]
                    : [""]
                }
                rightSection={
                  <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
                }
                withAsterisk
              />
            )}
          />
        </Grid.Col>
      </Grid>
      <Grid mt="md">
        <Grid.Col>
          <Controller
            name="denialReason"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="Denial Reason"
                placeholder="Select denial reason"
                data-testid="denial-reason-input"
                searchable
                error={errors.denialReason?.message}
                data={denialReasons}
                withAsterisk
              />
            )}
          />
          {selectedDenialReason === DenialReason.STEP_THERAPY_REQUIRED && (
            <div className={styles.stepTherapyDenialTip}>
              <Tip>
                <Tip.Heading>Important Tip:</Tip.Heading>
                <Tip.Caption>
                  Help your patients by including the step therapy medications
                  required by their insurer in your invitation email below.
                </Tip.Caption>
              </Tip>
            </div>
          )}
        </Grid.Col>
      </Grid>

      {isGlp1Condition(selectedCondition) && (
        <>
          <Grid mt="md">
            <Grid.Col>
              <div className={styles.fdaDisclaimer}>
                <Controller
                  name="meetsFdaGuidelines"
                  control={control}
                  render={() => (
                    <span className={`${styles.checkbox}`}>
                      <Checkbox
                        {...register("meetsFdaGuidelines")}
                        error={errors.meetsFdaGuidelines?.message}
                        label="This patient meets the FDA guidelines for GLP-1"
                        classNames={{
                          inner: styles.checkboxContainer,
                          input: styles.checkboxInput,
                          icon: styles.checkboxIcon,
                        }}
                      />
                    </span>
                  )}
                />
                <span>
                  <Popover
                    withArrow
                    styles={{
                      arrow: {
                        background: "#000",
                      },
                      dropdown: {
                        background: "#000",
                        borderRadius: "4px",
                        padding: "8px",
                      },
                    }}
                    width="350px"
                  >
                    <Popover.Target>
                      <FontAwesomeIcon
                        color="#29A7D3"
                        icon={faInfoCircle}
                        title="Info"
                      />
                    </Popover.Target>
                    <Popover.Dropdown>
                      <FdaEligibilityPopoverContent
                        condition={selectedCondition}
                      />
                    </Popover.Dropdown>
                  </Popover>
                </span>
              </div>
            </Grid.Col>
          </Grid>
          {selectedDenialReason !== DenialReason.FORCED_MEDICATION_SWITCH && (
            <Grid mt="md">
              <Grid.Col>
                <Controller
                  shouldUnregister
                  name="subDenialReason"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      searchable
                      label="Additional denial detail"
                      placeholder="Select one"
                      data-testid="additional-denial-detail-input"
                      data={Object.values(
                        subDenialReasonOptionsByCondition[selectedCondition],
                      )}
                    />
                  )}
                />
              </Grid.Col>
            </Grid>
          )}
        </>
      )}
      <Grid>
        <Grid.Col>
          <Anchor
            href="https://www.getclaimable.com/provider-waitlist"
            target="_blank"
          >
            <Text
              className={styles.waitlistLink}
              fz="lg"
              c="black"
              ta="center"
              mb="lg"
              td="underline"
            >
              <FontAwesomeIcon
                icon={faQuestionCircle}
                style={{ marginRight: "8px" }}
              />
              {"Don’t see what you’re looking for?"}
            </Text>
          </Anchor>
        </Grid.Col>
      </Grid>
      <hr className={`${styles.borderBottom} ${styles.patientInfoDivider}`} />
      <h2 className={styles.formHeader}>{"Patient info"}</h2>
      <Grid>
        <Grid.Col>
          <TextInput
            {...register("firstName")}
            label="First Name"
            data-testid="patient-first-name-input"
            error={errors.firstName?.message}
            withAsterisk
          />
        </Grid.Col>
      </Grid>
      <Grid mt="md">
        <Grid.Col>
          <TextInput
            {...register("lastName")}
            data-testid="patient-last-name-input"
            label="Last Name"
            error={errors.lastName?.message}
            withAsterisk
          />
        </Grid.Col>
      </Grid>
      <Grid mt="md">
        <Grid.Col>
          <TextInput
            {...register("patientEmail")}
            label="Email Address"
            data-testid="patient-email-input"
            error={errors.patientEmail?.message}
            withAsterisk
          />
        </Grid.Col>
      </Grid>
      <Grid mt="md">
        <Grid.Col>
          <TextInput
            label="Mobile Number"
            {...register("phoneNumber")}
            onChange={(event) => {
              register("phoneNumber").onChange(event);
              trigger("hasConsentedToSMSNotifications");
            }}
            error={errors.phoneNumber?.message}
            leftSection={
              <FontAwesomeIcon
                icon={faMobile}
                style={{ height: "30px", width: "16px" }}
              />
            }
          />
          <div className={`${styles.checkbox}`}>
            <Checkbox
              {...register("hasConsentedToSMSNotifications")}
              onChange={(event) => {
                register("hasConsentedToSMSNotifications").onChange(event);
                trigger("phoneNumber");
              }}
              error={errors.hasConsentedToSMSNotifications?.message}
              label="I confirm that I have obtained the patient’s consent to share their contact information and allow Claimable to send them SMS notifications about their appeal."
              classNames={{
                inner: styles.checkboxContainer,
                input: styles.checkboxInput,
                icon: styles.checkboxIcon,
              }}
            />
          </div>
        </Grid.Col>
      </Grid>
      <Grid mt="md">
        <Grid.Col>
          <TextInput
            {...register("dateOfBirth")}
            data-testid="patient-birth-date-input"
            type="date"
            label="Date of birth"
            error={errors.dateOfBirth?.message}
            withAsterisk
          />
        </Grid.Col>
      </Grid>
      {isFacilityRequired && (
        <Grid mt="md">
          <Grid.Col>
            <Controller
              name="providerFacilityId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Facility"
                  placeholder="Select a facility"
                  data={providerFacilities.map((facility) => ({
                    value: facility.providerFacilityId,
                    label: facility.facilityName,
                  }))}
                  error={errors.providerFacilityId?.message}
                  withAsterisk
                  required
                />
              )}
            />
          </Grid.Col>
        </Grid>
      )}
      <Grid mt="md">
        <Grid.Col>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="State"
                data-testid="patient-state-input"
                searchable
                error={errors.state?.message}
                data={states.map(({ state, stateCode }) => ({
                  value: stateCode,
                  label: state,
                }))}
                classNames={{ input: styles.input, label: styles.inputLabel }}
                withAsterisk
              />
            )}
          />
        </Grid.Col>
      </Grid>
      <Grid mt="md">
        <Grid.Col>
          <InputLabel htmlFor="message">
            <Text fz="xl" fw="500" className="textarea_label" mb="sm">
              {"Personal message for your patient"}
            </Text>
            <Textarea
              {...register("message")}
              placeholder="We’ve teamed up with Claimable to help fight insurance denials. Good news—we’ve already started an appeal for you! If you need any support along the way just reach out to Claimable at support@getclaimable.com. They’re here to help! "
              resize="vertical"
              size="xl"
              radius="lg"
              fz="md"
              data-testid="provider-message-input"
              error={errors.message?.message}
            />
          </InputLabel>
          <div className={`${styles.messageContainer}`}>
            <Text
              className={styles.disclaimerText}
              fz="sm"
              c="almost"
              ta="left"
            >
              <FontAwesomeIcon
                size="lg"
                icon={faCircleExclamation}
                style={{ paddingRight: "8px" }}
              />
              {
                "This message will be shown to your patient in the email invitation they receive"
              }
            </Text>
          </div>
        </Grid.Col>
      </Grid>
    </form>
  );
}

export default ReferralForm;
